// TEMPORARY (temp/linked-customer): "Linked Customer Registration": phone + OTP (same as the normal registration),
// then the customer is created and linked to the Booking Form; this modal follows the link until it settles.
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../Helpers/Handler";
import { textCapitalize } from "../../Helpers/Utils";
import { linkedApi, BASE } from "./linkedApi";
import { cleanDigits } from "./linkedLogic";
import LinkedStatusPanel from "./LinkedStatusPanel";
import LinkedPhonesModal from "./LinkedPhonesModal";

const LinkedRegisterModal = ({ onClose, onOpenCustomer, base = BASE, isAdmin = false, sendOtpUrl = "/staff/customer/registration/phone-otp" }) => {
    const api = useRef(linkedApi(base)).current;
    const modalRef = useRef(null);
    const [phone, setPhone] = useState("");
    const [otp, setOtp] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [busy, setBusy] = useState(false);
    const [created, setCreated] = useState(null); // { customerId, linkState }
    const [showPhones, setShowPhones] = useState(false);

    useEffect(() => {
        const el = modalRef.current;
        if (!el || !window.bootstrap) return undefined;
        const instance = window.bootstrap.Modal.getOrCreateInstance(el);
        const hidden = () => onClose?.();
        el.addEventListener("hidden.bs.modal", hidden);
        instance.show();
        return () => { el.removeEventListener("hidden.bs.modal", hidden); instance.hide(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sendOtp = async () => {
        if (phone.length !== 10) return toast.error("Enter a valid 10 digit phone number");
        setBusy(true);
        const r = await Handler({ method: "post", url: sendOtpUrl, data: { phone } });
        setBusy(false);
        if (r?.success) { toast.success(r?.data?.message || "OTP sent"); setOtpSent(true); } else toast.error(textCapitalize(r?.data?.message || "Unable to send OTP"));
    };

    const verify = async () => {
        if (otp.length !== 6) return toast.error("Enter the 6 digit OTP");
        setBusy(true);
        const r = await api.verifyRegistration(phone, otp);
        setBusy(false);
        if (r.ok) { toast.success(r.message || "Customer created"); setCreated(r.data); } else toast.error(textCapitalize(r.message || "Unable to verify OTP"));
    };

    return (
        <>
            <div className="modal fade" tabIndex="-1" ref={modalRef} aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content border-0 rounded-4">
                        <div className="modal-header">
                            <h5 className="modal-title fw-semibold">Linked Customer Registration</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            {!created ? (
                                <>
                                    <p className="small text-muted">Creates the customer here and in the Booking Form. Use the customer's own number: a real OTP is sent.</p>
                                    <label className="form-label">Phone number</label>
                                    <div className="input-group mb-3">
                                        <input type="text" inputMode="numeric" className="form-control" value={phone} disabled={otpSent}
                                            onChange={(e) => setPhone(cleanDigits(e.target.value, 10))} placeholder="10 digit mobile number" />
                                        <button type="button" className="btn btn-primary" onClick={sendOtp} disabled={busy || otpSent}>{otpSent ? "Sent" : busy ? "Sending..." : "Send OTP"}</button>
                                    </div>
                                    {otpSent ? (
                                        <>
                                            <label className="form-label">Enter OTP</label>
                                            <input type="text" inputMode="numeric" className="form-control" value={otp} onChange={(e) => setOtp(cleanDigits(e.target.value, 6))} placeholder="6 digit code" />
                                            <button type="button" className="btn btn-link px-0 mt-2" onClick={() => { setOtpSent(false); setOtp(""); }}>Use a different number</button>
                                        </>
                                    ) : ""}
                                </>
                            ) : (
                                <>
                                    <div className="mb-2">Customer <strong>{created.customerId}</strong> created.</div>
                                    {created.linkState === "pending"
                                        ? <LinkedStatusPanel customerId={created.customerId} base={base} isAdmin={isAdmin} onVerifyPhones={() => setShowPhones(true)} />
                                        : <div className="alert alert-warning py-2 mb-0">The customer was created, but linking could not start. Open the customer and use "Link to Booking Form".</div>}
                                </>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">{created ? "Close" : "Cancel"}</button>
                            {!created && otpSent ? <button type="button" className="btn btn-primary" onClick={verify} disabled={busy}>{busy ? "Verifying..." : "Verify & create"}</button> : ""}
                            {created ? <button type="button" className="btn btn-outline-primary" onClick={() => onOpenCustomer?.(created.customerId)}>Open customer</button> : ""}
                        </div>
                    </div>
                </div>
            </div>
            {showPhones && created ? <LinkedPhonesModal customerId={created.customerId} base={base} onClose={() => setShowPhones(false)} /> : ""}
        </>
    );
};

export default LinkedRegisterModal;
